import { useState, useEffect, useMemo } from 'react';
import {
  Group, Text, Badge, Stack, Center, Tabs, Table, Button, Select, NumberInput, Textarea, ActionIcon,
  Modal, SimpleGrid, Switch, Alert,
} from '@mantine/core';
import { MonthPickerInput, TimeInput, DateTimePicker, DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useSearchParams } from 'react-router-dom';
import {
  IconCash, IconCalendarTime, IconClockHour4, IconHistory, IconTrash, IconPlus, IconLogin2,
  IconLogout2, IconDeviceFloppy, IconPencil, IconAlertTriangle, IconClockEdit,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import api from '../../services/api';
import { formatSom, formatDate, tzDateKey } from '../../lib/format';
import { confirmDelete } from '../../lib/confirm';
import { SectionCard } from '../SectionCard';
import { SalaryAdjustments } from '../SalaryAdjustments';
import type { KunHisobiJson } from './IshVaqtiJadvali';
import { KunlarJadvali, type KunQatoriJson } from './KunlarJadvali';
import { OylarJadvali } from './OylarJadvali';
import { KetishModal, type OchiqYozuv } from './KetishModal';
import { MaoshBerishModal } from './MaoshBerishModal';
import { monthToDate, thisMonth, ym, sanaLocale, OY_FORMATI, monthLabel } from '../../lib/month';

interface Salary { type: 'FIXED' | 'PER_LESSON' | 'HOURLY'; baseAmount: number; rate: number; dailyHours?: number; note?: string | null; startDate?: string | null; endDate?: string | null }
/** FIXED, qoida yoqilgan oy — backend `FiksNatija` (kunlar jadvalisiz, `ishVaqti` da), spec 2026-10-21 §4. */
interface Fiks {
  smenasiz: boolean; kutilganKunlar: number; kunlik: number;
  otganKunlar: number; kelganKunlar: number; kelmaganKunlar: number; tashqariKunlar: number;
  kechDaqiqa: number; ertaDaqiqa: number; ushlangan: number; kelmaganPul: number; tashqariPul: number;
  bugungiQism: number; tuzatish: number; tuzatishKelajak: number;
  yozilgan: number; prognoz: number; ketishBelgilanmagan: number;
  /** Yakunlangan kutilgan kunlarning birortasida ham kelmagan (bugun ham ishda emas) — Maosh sahifasidagi `NO_LOGS`. */
  yozuvYoq?: boolean;
  /** Bugun kelgan, smena hali tugamagan — kunlar jadvalida bugungi qator "davom etmoqda". */
  bugunYakunlanmagan?: boolean;
}
interface Compute {
  configured: boolean; type: string | null; amount: number; paid: number; remaining: number;
  /** Bugungacha yozilgan (FIXED qoida oyida; boshqa hollarda `amount`). */
  yozilgan?: number;
  /** Oy oxiri prognozi (FIXED qoida oyida; boshqa hollarda `amount`). */
  prognoz?: number;
  /** amount − paid, ISHORALI: manfiy — ortiqcha berilgan (`remaining` = max(0, qolgan)). */
  qolgan?: number;
  /** FIXED, qoida yoqilgan oy — to'liq hisob; aks holda `null`. */
  fiks?: Fiks | null;
  breakdown: { rate?: number; baseAmount?: number; lessons?: number; hours?: number; dailyHours?: number; actualMinutes?: number; openLogs?: number; prorate?: number; ishKunlari?: number; ulush?: number; kelmadi?: number; kechikishDaqiqa?: number; ertaKetishDaqiqa?: number };
  /**
   * docs/ELEKTRON-JURNAL.md §6.7 — PER_LESSON maoshi FAOL o'quv yili sinflari jadvalidan
   * hisoblanadi. Faol yil yo'q yoki unda sinf bo'lmasa hisob jimgina 0 so'm berardi;
   * endi sabab qaytadi va shu yerda ochiq ko'rsatiladi.
   */
  warning?: 'NO_ACTIVE_YEAR' | 'ACTIVE_YEAR_HAS_NO_CLASSES' | 'NO_SHIFT' | null;
  activeYear?: { id: string; name: string } | null;
  /** Shu oydagi ish kunlari (bayramlar chiqarilgan). */
  ishKunlari?: number;
  bayramKunlari?: number;
  /** Bir kunlik maosh — faqat FIXED uchun (soatbay/darsbayda o'lchov birligi boshqa). */
  kunlik?: number;
  tuzatishlar?: { id: string; date: string; amount: number; reason: string }[];
  tuzatishJami?: number;
  /** HOURLY: 'YYYY-MM-DD' (Toshkent) → shu kun hisobga olingan butun soat (backend `lib/soatbay.ts`). */
  kunlikSoat?: Record<string, number> | null;
  /** Hisob qoidasi (backend spec 2026-10-14): ISH_VAQTI — belgilangan ish vaqti bo'yicha. */
  qoida?: 'ISH_VAQTI' | 'ESKI';
  ishVaqti?: KunHisobiJson[] | null;
  /** Oyning har kuni — keldi/ketdi, ishlagan vaqt, kun puli (backend `lib/kunlarJadvali.ts`). */
  kunlar?: KunQatoriJson[] | null;
}
interface Shift { id: string; dayOfWeek: number; startTime: string; endTime: string }
interface WorkLog { id: string; checkIn: string; checkOut: string | null; note?: string | null }
interface SalaryHistory { items: { id: string; amount: number; spentAt: string; forMonth?: string | null; note?: string | null }[]; total: number }

/** URL `?oy=` qiymati — faqat aniq "YYYY-MM" (Maosh sahifasidan kelgan havola). */
const OY_RE = /^\d{4}-(0[1-9]|1[0-2])$/;
const minutesBetween = (a: string, b: string) => Math.floor((+new Date(b) - +new Date(a)) / 60_000);
/** Daqiqa → "8 s 42 daq" (o'nli soat "8.7" 8:42 ga o'xshab chalg'itardi). */
const fmtHM = (min: number, t: TFunction) => t('staffDetail.hm', { h: Math.floor(min / 60), m: min % 60 });
export const apiErr = (e: unknown, fallback: string) =>
  (e as { response?: { data?: { error?: string } } })?.response?.data?.error ?? fallback;

/**
 * Xodimning MAOSH / SMENA / DAVOMAT / MAOSH TARIXI bo'limi — oy tanlagich + 4 tab.
 * Xodim (`StaffDetail`) va o'qituvchi (`TeacherDetail`) sahifalarida YAGONA komponent:
 * o'qituvchi ham xodim (maoshi, smenasi, kelib-ketishi `/payroll/*` da), ilgari bu
 * ma'lumot faqat xodim sahifasida ko'rinardi.
 */
/**
 * `money` — maosh va maosh tarixi (pul) ko'rinsinmi. Moliyani faqat direktor va
 * buxgalter ko'radi (`permissions.ts` → `payments` bo'limi); administratorga
 * faqat smena va kelish-ketish qoladi — backend ham `/expenses` ni unga yopgan.
 */
export function StaffPayrollSection({ userId, money = true }: { userId: string; money?: boolean }) {
  const { t, i18n } = useTranslation();
  // Boshlang'ich oy — Maosh sahifasidan `?oy=YYYY-MM` bilan kelinsa o'sha oy, aks holda joriy oy
  const [params] = useSearchParams();
  const [month, setMonth] = useState(() => {
    const oy = params.get('oy');
    return oy && OY_RE.test(oy) ? oy : thisMonth();
  });
  const dayNames = [t('days.1'), t('days.2'), t('days.3'), t('days.4'), t('days.5'), t('days.6'), t('days.7')];
  return (
    <>
      <MonthPickerInput
        label={t('staffDetail.month')}
        value={monthToDate(month)}
        onChange={(d) => d && setMonth(ym(d))}
        locale={sanaLocale(i18n.language)}
        valueFormat={OY_FORMATI} w={200}
      />

      <Tabs defaultValue={money ? 'salary' : 'shifts'}>
        <Tabs.List mb="md">
          {money && <Tabs.Tab value="salary" leftSection={<IconCash size={16} />}>{t('staffDetail.salary')}</Tabs.Tab>}
          <Tabs.Tab value="shifts" leftSection={<IconCalendarTime size={16} />}>{t('staffDetail.shifts')}</Tabs.Tab>
          <Tabs.Tab value="logs" leftSection={<IconClockHour4 size={16} />}>{t('staffDetail.logs')}</Tabs.Tab>
          {money && <Tabs.Tab value="history" leftSection={<IconHistory size={16} />}>{t('staffDetail.history')}</Tabs.Tab>}
        </Tabs.List>

        {money && <Tabs.Panel value="salary"><SalaryTab userId={userId} month={month} setMonth={setMonth} /></Tabs.Panel>}
        <Tabs.Panel value="shifts"><ShiftsTab userId={userId} dayNames={dayNames} /></Tabs.Panel>
        <Tabs.Panel value="logs"><LogsTab userId={userId} month={month} money={money} /></Tabs.Panel>
        {money && <Tabs.Panel value="history"><HistoryTab userId={userId} /></Tabs.Panel>}
      </Tabs>
    </>
  );
}

/*
 * Tab komponentlari MODUL DARAJASIDA turadi (ilgari `StaffDetail` ichida e'lon qilingan edi).
 * Ota komponent ichidagi `function` har renderda YANGI komponent turini yasar edi, React esa
 * eski daraxtni tashlab qaytadan mount qilardi — natijada tab ichidagi lokal holat (ochiq/yopiq
 * modal, "faqat ochiq" switch'i, yil filtri, forma qiymatlari, scroll) oy o'zgarganda yoki
 * istalgan query yangilanganda yo'qolib, inputlar fokusdan chiqib ketardi.
 * Kerakli ma'lumot ANIQ props orqali uzatiladi (userId / month / dayNames); `t` va `qc` esa
 * kontekst qiymatlari — ular prop sifatida emas, o'z hook'lari orqali olinadi (ayni qiymat).
 */

// ─── Maosh (config + hisob + to'lash) ───
function SalaryTab({ userId, month, setMonth }: { userId: string; month: string; setMonth: (m: string) => void }) {
  const { t } = useTranslation();
  const oyNomi = monthLabel(month, t('monthsFull', { returnObjects: true }) as string[]);
  const qc = useQueryClient();
  const { data: cfg } = useQuery({ queryKey: ['salary-cfg', userId], queryFn: async () => (await api.get<Salary | null>(`/payroll/salary/${userId}`)).data });
  const { data: comp } = useQuery({ queryKey: ['salary-compute', userId, month], queryFn: async () => (await api.get<Compute>(`/payroll/compute/${userId}`, { params: { month } })).data });
  const [payOpen, payH] = useDisclosure(false);
  // Ish vaqti jadvalidagi "Bonus" — kunlik tuzatish oynasini shu kun va izoh bilan ochadi
  const [bonusTaklif, setBonusTaklif] = useState<{ kun: string; sabab: string; key: number } | null>(null);
  const ishVaqtiQoidasi = comp?.qoida === 'ISH_VAQTI';
  /* FIXED, qoida yoqilgan oy — yozilgan / berilgan / qolgan va prognoz (Maosh sahifasi bilan
     AYNI hisob: backend `fiksKirishi`). `qolgan` ISHORALI; eski serverda yo'q bo'lsa `remaining`. */
  const fiks = comp?.fiks ?? null;
  const qolgan = comp ? (comp.qolgan ?? comp.remaining) : 0;
  // Ish davridan tashqari kunlar (ishga kirishdan oldin / ketgandan keyin) — summani kamaytiradi
  const tashqariKun = fiks?.tashqariKunlar ?? 0;
  // Oy yoki xodim almashsa eski taklif oynani o'z-o'zidan ochmasin
  useEffect(() => { setBonusTaklif(null); }, [userId, month]);

  const cfgForm = useForm({ initialValues: { type: 'FIXED' as Salary['type'], baseAmount: 0, rate: 0, dailyHours: 8, note: '', startDate: null as Date | null, endDate: null as Date | null } });
  // cfg serverdan kelganda formani to'ldirish (bir marta)
  useEffect(() => {
    if (cfg) cfgForm.setValues({
      type: cfg.type, baseAmount: cfg.baseAmount, rate: cfg.rate, dailyHours: cfg.dailyHours ?? 8, note: cfg.note ?? '',
      startDate: cfg.startDate ? new Date(cfg.startDate) : null,
      endDate: cfg.endDate ? new Date(cfg.endDate) : null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cfg]);

  const saveCfg = useMutation({
    mutationFn: (v: typeof cfgForm.values) => api.put(`/payroll/salary/${userId}`, v),
    onSuccess: () => {
      notifications.show({ color: 'green', message: t('common.save') });
      qc.invalidateQueries({ queryKey: ['salary-cfg', userId] });
      qc.invalidateQueries({ queryKey: ['salary-compute', userId] });
      qc.invalidateQueries({ queryKey: ['salary-oylar', userId] });
      // Oylik summa / turi / ish davri — Xodimlar ro'yxati va Maosh sahifasi raqamlari ham
      qc.invalidateQueries({ queryKey: ['payroll-summary'] });
      qc.invalidateQueries({ queryKey: ['payroll-oylik'] });
    },
    onError: (e) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) }),
  });

  const type = cfgForm.values.type;

  /* Maosh sozlamasi — bir marta kiritiladi, shuning uchun eng pastda */
  const sozlama = (
      <SectionCard title={t('staffDetail.salaryConfig')} icon={IconCash}>
        <form onSubmit={cfgForm.onSubmit((v) => saveCfg.mutate(v))}>
          <Stack>
            <Select
              label={t('staffDetail.salaryType')} allowDeselect={false}
              data={[
                { value: 'FIXED', label: t('salaryType.FIXED') },
                { value: 'PER_LESSON', label: t('salaryType.PER_LESSON') },
                { value: 'HOURLY', label: t('salaryType.HOURLY') },
              ]}
              {...cfgForm.getInputProps('type')}
            />
            {type === 'FIXED' ? (
              <>
                <NumberInput label={t('staffDetail.monthlyAmount')} min={0} step={100000} thousandSeparator=" " suffix={` ${t('currency.som')}`} {...cfgForm.getInputProps('baseAmount')} />
                <Group grow>
                  <DateInput label={t('staffDetail.hireDate')} placeholder={t('staffDetail.prorateHint')} valueFormat="DD.MM.YYYY" clearable {...cfgForm.getInputProps('startDate')} />
                  <DateInput label={t('staffDetail.leaveDate')} placeholder={t('staffDetail.prorateHint')} valueFormat="DD.MM.YYYY" clearable {...cfgForm.getInputProps('endDate')} />
                </Group>
              </>
            ) : (
              <NumberInput label={type === 'PER_LESSON' ? t('staffDetail.perLesson') : t('staffDetail.perHour')} min={0} step={10000} thousandSeparator=" " suffix={` ${t('currency.som')}`} {...cfgForm.getInputProps('rate')} />
            )}
            {/* Soatbay: kuniga ko'pi bilan shuncha BUTUN soat hisoblanadi (backend `lib/soatbay.ts`) */}
            {type === 'HOURLY' && (
              <NumberInput
                label={t('staffDetail.dailyHours')} description={t('staffDetail.dailyHoursHint')}
                min={1} max={24} step={1} allowDecimal={false} clampBehavior="strict"
                {...cfgForm.getInputProps('dailyHours')}
              />
            )}
            <Textarea label={t('staffDetail.note')} autosize minRows={1} {...cfgForm.getInputProps('note')} />
            <Button type="submit" leftSection={<IconDeviceFloppy size={16} />} loading={saveCfg.isPending}>{t('common.save')}</Button>
          </Stack>
        </form>
      </SectionCard>
  );

  return (
    <Stack>
      {/* Oylar bo'yicha — qaysi oyda qancha ishlagan; qator bosilsa oy tanlanadi */}
      <OylarJadvali userId={userId} month={month} onSelect={setMonth} />

    <SimpleGrid cols={{ base: 1, md: 2 }}>
      {/* Hisob-kitob */}
      <SectionCard
        title={`${t('staffDetail.calculation')} · ${oyNomi}`}
        icon={IconCalendarTime}
        right={comp?.configured ? <Badge variant="light">{t(`salaryType.${comp.type}`)}</Badge> : undefined}
      >
        {!comp?.configured ? (
          <Text c="dimmed" size="sm">{t('staffDetail.notConfigured')}</Text>
        ) : (
          <Stack gap="xs">
            {/* §6.7: 0 so'm sababini jimgina qoldirmaymiz */}
            {comp.warning && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
                <Text fz="sm" lh={1.5}>
                  {comp.warning === 'NO_ACTIVE_YEAR'
                    ? t('staffDetail.payrollWarnNoYear')
                    : comp.warning === 'NO_SHIFT'
                      // FIXED smenasiz: kelgan kun to'liq (spec §4.4); soatbayda — eski matn
                      ? (comp.type === 'FIXED' ? t('ishVaqti.noShiftFixed') : t('ishVaqti.noShift'))
                      : t('staffDetail.payrollWarnNoClasses', { name: comp.activeYear?.name ?? '' })}
                </Text>
              </Alert>
            )}
            {/* Bu oyda bitta ham kelish yozuvi yo'q — Maosh sahifasidagi "Yozuv yo'q" belgisi bilan bir xil */}
            {fiks?.yozuvYoq && (
              <Alert color="yellow" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
                <Text fz="sm" lh={1.5}>{t('payrollPage.warnNoLogsHint')}</Text>
              </Alert>
            )}
            {/* Oylik bor, lekin kutilgan kun 0 — Maosh sahifasidagi NO_WORKDAYS belgisi bilan bir xil */}
            {fiks && fiks.kutilganKunlar === 0 && (comp.breakdown.baseAmount ?? 0) > 0 && (
              <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />} p="xs">
                <Text fz="sm" lh={1.5}>{t('payrollPage.warnNoWorkdaysHint')}</Text>
              </Alert>
            )}
            {fiks ? (
              /* QOIDA YOQILGAN FIXED (spec 2026-10-21 §4): Maosh sahifasidagi qator bilan bir xil */
              <>
                {/* "18/22" nima ekani tushunarsiz edi — oydagi ish kunlari va kelgan kunlar alohida */}
                <Row label={t('ishVaqti.workDays')} value={String(fiks.kutilganKunlar)} />
                <Row label={t('kunlar.came')} value={t('kunlar.daysOf', { n: fiks.kelganKunlar + (fiks.bugunYakunlanmagan ? 1 : 0), jami: fiks.otganKunlar + (fiks.bugunYakunlanmagan ? 1 : 0) })} color="teal.7" />
                <Row label={t('payrollPage.daily')} value={formatSom(fiks.kunlik)} />
                <Row label={t('payrollPage.absent')} value={`${fiks.kelmaganKunlar} · ${formatSom(fiks.kelmaganPul)}`} color={fiks.kelmaganKunlar > 0 ? 'red.7' : undefined} />
                <Row label={t('payrollPage.lateEarly')} value={t('payrollPage.lateEarlyValue', { kech: fiks.kechDaqiqa, erta: fiks.ertaDaqiqa })} />
                <Row label={t('payrollPage.withheld')} value={formatSom(fiks.ushlangan)} color={fiks.ushlangan > 0 ? 'red.7' : undefined} />
                <Row
                  label={t('payrollPage.adjustment')}
                  value={fiks.tuzatish > 0 ? `+${formatSom(fiks.tuzatish)}` : fiks.tuzatish < 0 ? `−${formatSom(-fiks.tuzatish)}` : '—'}
                  color={fiks.tuzatish > 0 ? 'teal.7' : fiks.tuzatish < 0 ? 'red.7' : undefined}
                />
                {tashqariKun > 0 && <Text size="xs" c="orange.7">{t('ishVaqti.outsidePeriod', { n: tashqariKun })}</Text>}
                <Row label={t('payrollPage.accrued')} value={formatSom(comp.yozilgan ?? comp.amount)} strong />
                {fiks.bugungiQism > 0 && <Text size="xs" c="dimmed" ta="right">{t('payrollPage.today', { sum: formatSom(fiks.bugungiQism) })}</Text>}
                <Row label={t('payrollPage.paid')} value={formatSom(comp.paid)} />
                <Row
                  label={t('payrollPage.remaining')}
                  value={qolgan < 0
                    // Yozilgan manfiy (jarima ishlanganidan katta) — "ortiqcha berilgan" emas; Maosh sahifasi bilan bir xil
                    ? t((comp.yozilgan ?? comp.amount) < 0 ? 'payrollPage.overWithheldHint' : 'payrollPage.overpaidHint', { sum: formatSom(-qolgan) })
                    : formatSom(qolgan)}
                  strong
                  color={qolgan < 0 ? 'orange.8' : qolgan > 0 ? 'red.7' : 'teal.7'}
                />
                <Text size="xs" c="dimmed">{t('payrollPage.forecast')}: {formatSom(comp.prognoz ?? fiks.prognoz)}</Text>
              </>
            ) : (
              <>
                {comp.breakdown.lessons != null && (
                  <>
                    <Row label={t('staffDetail.lessons')} value={`${comp.breakdown.lessons} × ${formatSom(comp.breakdown.rate ?? 0)}`} />
                    <Text size="xs" c="dimmed">{t('staffDetail.plannedHint')}</Text>
                  </>
                )}
                {comp.breakdown.hours != null && (
                  <>
                    <Row label={t('staffDetail.hours')} value={`${comp.breakdown.hours} × ${formatSom(comp.breakdown.rate ?? 0)}`} />
                    <Text size="xs" c="dimmed">
                      {t('staffDetail.hoursRule', { n: comp.breakdown.dailyHours ?? 8 })}
                      {comp.breakdown.actualMinutes != null && ` · ${t('staffDetail.actualTime')}: ${fmtHM(comp.breakdown.actualMinutes, t)}`}
                    </Text>
                  </>
                )}
                {(comp.breakdown.openLogs ?? 0) > 0 && (
                  <Text size="xs" c="orange.7">{comp.breakdown.openLogs} {t('staffDetail.openExcluded')}</Text>
                )}
                {comp.breakdown.prorate != null && comp.breakdown.prorate < 1 && (
                  <Text size="xs" c="dimmed">{t('staffDetail.prorated')}: ×{comp.breakdown.prorate}</Text>
                )}
                {/* ISH KUNLARI VA KUNLIK MAOSH — fiks oylikda. Ilgari "oyiga 5 mln"
                    dan boshqa hech narsa ko'rinmasdi va bir kunlik jarima yoki
                    rag'batni qanday hisoblashni admin o'zi topib olishi kerak edi. */}
                {comp.type === 'FIXED' && comp.ishKunlari != null && (
                  <>
                    <Row
                      label={t('staffDetail.workDays')}
                      value={
                        comp.bayramKunlari
                          ? t('staffDetail.workDaysWithHoliday', { kun: comp.ishKunlari, bayram: comp.bayramKunlari })
                          : String(comp.ishKunlari)
                      }
                    />
                    <Row label={t('staffDetail.perDay')} value={formatSom(comp.kunlik ?? 0)} />
                  </>
                )}
                {!!comp.tuzatishJami && (
                  <Row
                    label={t('staffDetail.adjustments')}
                    value={`${comp.tuzatishJami > 0 ? '+' : ''}${formatSom(comp.tuzatishJami)}`}
                    color={comp.tuzatishJami > 0 ? 'teal.7' : 'red.7'}
                  />
                )}
                <Row label={t('staffDetail.computed')} value={formatSom(comp.amount)} strong />
                <Row label={t('staffDetail.paidThisMonth')} value={formatSom(comp.paid)} />
                <Row label={t('staffDetail.remaining')} value={formatSom(comp.remaining)} strong color={comp.remaining > 0 ? 'red.7' : 'teal.7'} />
              </>
            )}
            {qolgan > 0 ? (
              <Button mt="xs" leftSection={<IconCash size={16} />} onClick={payH.open}>
                {t('payrollPage.pay')}
              </Button>
            ) : comp.amount > 0 ? (
              <Badge mt="xs" color="teal" variant="light" size="lg">{t('staffDetail.fullyPaid')}</Badge>
            ) : null}
          </Stack>
        )}
      </SectionCard>

      {comp?.configured && (
        <SalaryAdjustments userId={userId} month={month} kunlik={comp.kunlik} taklif={bonusTaklif} onTaklifTugadi={() => setBonusTaklif(null)} />
      )}
    </SimpleGrid>

      {/* Kunlar bo'yicha — har kuni keldi/ketdi, ishlagan vaqt va kun puli (har qanday maosh turida) */}
      {!!comp?.kunlar?.length && (
        <KunlarJadvali
          kunlar={comp.kunlar}
          oyNomi={oyNomi}
          kunbay={!!comp.configured && (comp.type === 'HOURLY' || !!fiks)}
          onBonus={ishVaqtiQoidasi ? (kun, ortiqcha) => setBonusTaklif({ kun, sabab: t('ishVaqti.bonusReason', { n: ortiqcha }), key: Date.now() }) : undefined}
        />
      )}

      {sozlama}

      {/* Maosh berish — Maosh sahifasi bilan YAGONA oyna (summa sukuti — qolgan) */}
      <MaoshBerishModal opened={payOpen} onClose={payH.close} userId={userId} month={month} qolgan={qolgan} />
    </Stack>
  );
}

// ─── Ish jadvali (shifts) ───
function ShiftsTab({ userId, dayNames }: { userId: string; dayNames: string[] }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: shifts } = useQuery({ queryKey: ['shifts', userId], queryFn: async () => (await api.get<Shift[]>(`/payroll/shifts/${userId}`)).data });
  // Smena maoshga ta'sir qiladi (ish vaqti qoidasi): Maosh yorlig'i, ro'yxat va Maosh sahifasi summalari ham yangilansin
  const yangilaHisob = () => {
    qc.invalidateQueries({ queryKey: ['salary-compute', userId] });
    qc.invalidateQueries({ queryKey: ['payroll-summary'] });
    qc.invalidateQueries({ queryKey: ['payroll-oylik'] });
  };
  const form = useForm({ initialValues: { dayOfWeek: '1', startTime: '09:00', endTime: '18:00' } });
  const addMut = useMutation({
    mutationFn: (v: typeof form.values) => api.post('/payroll/shifts', { userId, dayOfWeek: Number(v.dayOfWeek), startTime: v.startTime, endTime: v.endTime }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts', userId] }); yangilaHisob(); },
    onError: () => notifications.show({ color: 'red', message: t('staffDetail.shiftError') }),
  });
  const delMut = useMutation({ mutationFn: (sid: string) => api.delete(`/payroll/shifts/${sid}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ['shifts', userId] }); yangilaHisob(); } });

  return (
    <SectionCard title={t('staffDetail.shifts')} icon={IconCalendarTime}>
      <form onSubmit={form.onSubmit((v) => addMut.mutate(v))}>
        <Group align="flex-end" mb="md">
          <Select label={t('staffDetail.weekday')} data={dayNames.map((d, i) => ({ value: String(i + 1), label: d }))} allowDeselect={false} w={150} {...form.getInputProps('dayOfWeek')} />
          <TimeInput label={t('staffDetail.start')} {...form.getInputProps('startTime')} />
          <TimeInput label={t('staffDetail.end')} {...form.getInputProps('endTime')} />
          <Button type="submit" leftSection={<IconPlus size={16} />} loading={addMut.isPending}>{t('common.add')}</Button>
        </Group>
      </form>
      {!shifts?.length ? (
        <Text c="dimmed" size="sm">{t('staffDetail.noShifts')}</Text>
      ) : (
        <Stack gap="xs">
          {shifts.map((s) => (
            <Group key={s.id} justify="space-between" p="xs" style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 8 }}>
              <Group gap="sm">
                <Badge variant="light" w={110}>{dayNames[s.dayOfWeek - 1]}</Badge>
                <Text fw={600}>{s.startTime} – {s.endTime}</Text>
              </Group>
              <ActionIcon variant="subtle" color="red" onClick={() => delMut.mutate(s.id)} loading={delMut.isPending}><IconTrash size={16} /></ActionIcon>
            </Group>
          ))}
        </Stack>
      )}
    </SectionCard>
  );
}

// ─── Kelgan / ketgan ───
function LogsTab({ userId, month, money }: { userId: string; month: string; money: boolean }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { data: logs } = useQuery({ queryKey: ['logs', userId, month], queryFn: async () => (await api.get<WorkLog[]>('/payroll/logs', { params: { userId, month } })).data });
  /* SOATBAY: maoshga ketadigan kunlik butun soat — SERVER hisoblaydi (`lib/soatbay.ts`),
     bu yerda takrorlanmaydi. Maosh tab'i bilan AYNI query (kesh umumiy). Moliyani
     ko'rmaydigan rol (`money=false`) uchun so'ralmaydi — unga faqat haqiqiy vaqt. */
  const { data: comp } = useQuery({
    queryKey: ['salary-compute', userId, month],
    queryFn: async () => (await api.get<Compute>(`/payroll/compute/${userId}`, { params: { month } })).data,
    enabled: money,
  });
  const kunlikSoat = comp?.type === 'HOURLY' ? comp.kunlikSoat ?? null : null;
  // Ochiq (check-out qilinmagan) log — OYDAN MUSTAQIL: o'tgan oyda "osilib" qolgan bo'lsa ham topiladi
  const { data: openLogs } = useQuery({ queryKey: ['open-log', userId], queryFn: async () => (await api.get<WorkLog[]>('/payroll/logs', { params: { userId, open: 1 } })).data });
  const openLog = openLogs?.[0];
  // "Faqat ochiq" — serverning ?open=1 javobi (oydan mustaqil), oylik ro'yxat o'rniga ko'rsatiladi
  const [onlyOpen, setOnlyOpen] = useState(false);
  const rows = onlyOpen ? (openLogs ?? []) : (logs ?? []);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['logs', userId] });
    qc.invalidateQueries({ queryKey: ['open-log', userId] });
    // Kelish-ketish maoshga ta'sir qiladi — karta, Xodimlar ro'yxati va Maosh sahifasi
    qc.invalidateQueries({ queryKey: ['salary-compute', userId] });
    qc.invalidateQueries({ queryKey: ['salary-oylar', userId] });
    qc.invalidateQueries({ queryKey: ['payroll-summary'] });
    qc.invalidateQueries({ queryKey: ['payroll-oylik'] });
  };
  const onErr = (e: unknown) => notifications.show({ color: 'red', message: apiErr(e, t('common.error')) });

  const checkIn = useMutation({ mutationFn: () => api.post('/payroll/logs/check-in', { userId }), onSuccess: invalidate, onError: onErr });
  const checkOut = useMutation({ mutationFn: (lid: string) => api.post(`/payroll/logs/${lid}/check-out`, {}), onSuccess: invalidate, onError: onErr });
  const delMut = useMutation({ mutationFn: (lid: string) => api.delete(`/payroll/logs/${lid}`), onSuccess: invalidate, onError: onErr });

  // Yozuv vaqtini tuzatish / orqaga qo'yish (HOURLY aniqligi uchun)
  const [editing, setEditing] = useState<WorkLog | null>(null);
  // Ketish belgilanmagan yozuvga faqat ketgan soatini kiritish (to'liq tahrirdan soddaroq)
  const [ketish, setKetish] = useState<OchiqYozuv | null>(null);
  const editForm = useForm<{ checkIn: Date | null; checkOut: Date | null; note: string }>({ initialValues: { checkIn: null, checkOut: null, note: '' } });
  const openEdit = (l: WorkLog) => {
    editForm.setValues({ checkIn: new Date(l.checkIn), checkOut: l.checkOut ? new Date(l.checkOut) : null, note: l.note ?? '' });
    setEditing(l);
  };
  const editMut = useMutation({
    mutationFn: (v: typeof editForm.values) => api.patch(`/payroll/logs/${editing!.id}`, {
      checkIn: v.checkIn?.toISOString(),
      checkOut: v.checkOut ? v.checkOut.toISOString() : null,
      note: v.note || null,
    }),
    onSuccess: () => { invalidate(); setEditing(null); notifications.show({ color: 'green', message: t('common.save') }); },
    onError: onErr,
  });

  const fmtTime = (s: string) => new Date(s).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  // Kunning hisobga olingan soati kunning BIRINCHI ko'rinadigan qatorida (bir kunda bir nechta yozuv bo'lsa takrorlanmasin)
  const kunBoshi = new Set<string>();
  const birinchiQator = new Set(rows.filter((l) => { const k = tzDateKey(l.checkIn); if (kunBoshi.has(k)) return false; kunBoshi.add(k); return true; }).map((l) => l.id));
  const openInThisMonth = openLog && logs?.some((l) => l.id === openLog.id);

  return (
    <SectionCard
      p={0}
      title={`${t('staffDetail.logs')}${onlyOpen ? '' : ` · ${month}`}`}
      icon={IconClockHour4}
      right={(
        <>
          <Switch
            label={t('staffDetail.onlyOpen')}
            checked={onlyOpen}
            onChange={(e) => setOnlyOpen(e.currentTarget.checked)}
          />
          {openLog ? (
            <Button size="compact-sm" color="orange" leftSection={<IconLogout2 size={16} />} onClick={() => checkOut.mutate(openLog.id)} loading={checkOut.isPending}>{t('staffDetail.checkOut')}</Button>
          ) : (
            <Button size="compact-sm" color="teal" leftSection={<IconLogin2 size={16} />} onClick={() => checkIn.mutate()} loading={checkIn.isPending}>{t('staffDetail.checkIn')}</Button>
          )}
        </>
      )}
    >
      {/* Ochiq log boshqa oyda — ogohlantirish (aks holda ko'rinmay qolardi) */}
      {openLog && !openInThisMonth && !onlyOpen && (
        <Text px="md" pb="xs" size="xs" c="orange.7">{t('staffDetail.checkIn')}: {formatDate(openLog.checkIn)} — {!money ? t('ishVaqti.openPlain') : comp?.qoida === 'ISH_VAQTI' ? t('ishVaqti.openCounted') : t('staffDetail.openExcluded')}</Text>
      )}
      {!rows.length ? (
        <Center py="lg"><Text c="dimmed" size="sm">{onlyOpen ? t('staffDetail.noMatch') : t('staffDetail.noLogs')}</Text></Center>
      ) : (
        <Table.ScrollContainer minWidth={600}>
        <Table striped verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr><Table.Th>{t('staffDetail.date')}</Table.Th><Table.Th>{t('staffDetail.in')}</Table.Th><Table.Th>{t('staffDetail.out')}</Table.Th><Table.Th>{t('staffDetail.worked')}</Table.Th><Table.Th w={80} /></Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((l) => (
              <Table.Tr key={l.id}>
                <Table.Td>{formatDate(l.checkIn)}</Table.Td>
                <Table.Td><Badge color="teal" variant="light">{fmtTime(l.checkIn)}</Badge></Table.Td>
                <Table.Td>
                  {l.checkOut ? <Badge color="orange" variant="light">{fmtTime(l.checkOut)}</Badge> : (
                    <Button size="compact-xs" variant="light" color="orange" leftSection={<IconClockEdit size={13} />} onClick={() => setKetish({ id: l.id, checkIn: l.checkIn })}>
                      {t('kunlar.setOut')}
                    </Button>
                  )}
                </Table.Td>
                <Table.Td>
                  {!l.checkOut ? (
                    <Text c="dimmed" size="sm">…</Text>
                  ) : kunlikSoat ? (
                    <Group gap={8} wrap="nowrap">
                      {birinchiQator.has(l.id) && <Text fw={600} style={{ whiteSpace: 'nowrap' }}>{kunlikSoat[tzDateKey(l.checkIn)] ?? 0} {t('staffDetail.h')}</Text>}
                      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{fmtHM(minutesBetween(l.checkIn, l.checkOut), t)}</Text>
                    </Group>
                  ) : (
                    <Text fw={600} style={{ whiteSpace: 'nowrap' }}>{fmtHM(minutesBetween(l.checkIn, l.checkOut), t)}</Text>
                  )}
                </Table.Td>
                <Table.Td>
                  <Group gap={4} justify="flex-end" wrap="nowrap">
                    <ActionIcon variant="subtle" onClick={() => openEdit(l)} title={t('staffDetail.editLog')}><IconPencil size={15} /></ActionIcon>
                    <ActionIcon variant="subtle" color="red" onClick={() => confirmDelete(formatDate(l.checkIn), () => delMut.mutate(l.id))} loading={delMut.isPending}><IconTrash size={15} /></ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        </Table.ScrollContainer>
      )}

      <KetishModal yozuv={ketish} onClose={() => setKetish(null)} />
      <Modal opened={!!editing} onClose={() => setEditing(null)} title={t('staffDetail.editLog')}>
        <form onSubmit={editForm.onSubmit((v) => editMut.mutate(v))}>
          <Stack>
            <DateTimePicker label={t('staffDetail.in')} valueFormat="DD.MM.YYYY HH:mm" required {...editForm.getInputProps('checkIn')} />
            <DateTimePicker label={t('staffDetail.out')} valueFormat="DD.MM.YYYY HH:mm" clearable {...editForm.getInputProps('checkOut')} />
            <Textarea label={t('staffDetail.note')} autosize minRows={1} {...editForm.getInputProps('note')} />
            <Group justify="flex-end"><Button variant="default" onClick={() => setEditing(null)}>{t('common.cancel')}</Button><Button type="submit" loading={editMut.isPending}>{t('common.save')}</Button></Group>
          </Stack>
        </form>
      </Modal>
    </SectionCard>
  );
}

// ─── Maosh tarixi ───
function HistoryTab({ userId }: { userId: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({ queryKey: ['salary-history', userId], queryFn: async () => (await api.get<SalaryHistory>(`/payroll/history/${userId}`)).data });
  // Yil bo'yicha filtr — mijoz tomonda (server oxirgi 100 yozuvni qaytaradi)
  const [year, setYear] = useState<string | null>(null);
  const items = useMemo(() => data?.items ?? [], [data?.items]);
  const years = useMemo(
    () => Array.from(new Set(items.map((e) => String(new Date(e.spentAt).getFullYear())))).sort((a, b) => b.localeCompare(a)),
    [items]
  );
  const shown = useMemo(
    () => items.filter((e) => !year || String(new Date(e.spentAt).getFullYear()) === year),
    [items, year]
  );
  const shownTotal = useMemo(() => shown.reduce((n, e) => n + e.amount, 0), [shown]);

  return (
    <SectionCard
      p={0}
      title={t('staffDetail.history')}
      icon={IconHistory}
      right={(
        <>
          <Select
            placeholder={t('staffDetail.allYears')}
            data={years.map((y) => ({ value: y, label: y }))}
            value={year || null}
            onChange={setYear}
            clearable
            w={140}
            size="xs"
          />
          {year && <Badge variant="light" color="teal">{t('staffDetail.filteredTotal')}: {formatSom(shownTotal)}</Badge>}
          <Badge variant="light" color="blue">{t('staffDetail.totalPaid')}: {formatSom(data?.total ?? 0)}</Badge>
        </>
      )}
    >
      {!shown.length ? (
        <Center py="lg"><Text c="dimmed" size="sm">{items.length ? t('staffDetail.noMatch') : t('staffDetail.noHistory')}</Text></Center>
      ) : (
        <Table striped verticalSpacing="sm">
          <Table.Thead><Table.Tr><Table.Th>{t('staffDetail.date')}</Table.Th><Table.Th>{t('staffDetail.forMonth')}</Table.Th><Table.Th>{t('staffDetail.note')}</Table.Th><Table.Th ta="right">{t('staffDetail.amount')}</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {shown.map((e) => (
              <Table.Tr key={e.id}>
                <Table.Td>{formatDate(e.spentAt)}</Table.Td>
                <Table.Td>{e.forMonth ?? '—'}</Table.Td>
                <Table.Td><Text size="sm" lineClamp={1} maw={220}>{e.note ?? '—'}</Text></Table.Td>
                <Table.Td ta="right"><Text fw={700} c="green.7">{formatSom(e.amount)}</Text></Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </SectionCard>
  );
}

function Row({ label, value, strong, color }: { label: string; value: string; strong?: boolean; color?: string }) {
  return (
    <Group justify="space-between">
      <Text size="sm" c="dimmed">{label}</Text>
      <Text fw={strong ? 800 : 600} c={color}>{value}</Text>
    </Group>
  );
}
